<script setup>
import axios from 'axios';
import { onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '../../../stores/auth';
import Loading from '../../Loading.vue';

const router = useRouter();
const authStore = useAuthStore();

// Google tokens are Passport tokens (expire in ~1 month); keep the cookie a
// week like the rest of the platform.
const TOKEN_COOKIE_DAYS = 7;

function goToLogin(errorMessage) {
	delete axios.defaults.headers.common['Authorization'];
	if (errorMessage) {
		authStore.setUserAlreadyExistEmail(null);
	}
	router.replace('/login');
}

// SSO returns a one-time code, not the token, because the URL lands in the
// ingress access logs. Redeem it through this hub's SSO proxy; null if refused.
async function redeemCode(code) {
	try {
		const { data } = await axios.post('/api/sso/api/auth/google/exchange', { code });
		return data?.access_token || null;
	} catch (error) {
		console.error('Google sign-in code refused:', error);
		return null;
	}
}

// With nothing to redeem (an error from Google), still spend the sign-in's
// verifier: it's an HttpOnly cookie, so only the proxy can clear it.
function spendVerifier() {
	axios.post('/api/sso/api/auth/google/exchange', {}).catch(() => {});
}

onMounted(async () => {
	const params = new URLSearchParams(window.location.search);
	const code = params.get('code');
	const errorParam = params.get('error');
	const redeeming = code && !errorParam;
	if (!redeeming) spendVerifier();
	const token = redeeming ? await redeemCode(code) : null;

	if (errorParam || !token) {
		goToLogin(errorParam || (code ? 'Google sign-in failed. Please try again.' : null));
		return;
	}

	// Authenticate subsequent requests with the SSO-issued token.
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

	try {
		// Returning member already registered for this event → straight to the Hub.
		await authStore.retrieveUserData();
		authStore.login({ access_token: token, expires_in: TOKEN_COOKIE_DAYS, role: authStore.userData.role });
		router.replace('/');
	} catch (error) {
		const code = error.response?.data?.code;

		if (code === 'ACCESS_MANAGEMENT_BLOCKED') {
			// Authenticated via Google, but this hub's access list blocks the email.
			delete axios.defaults.headers.common['Authorization'];
			router.replace('/login?access_blocked=1');
			return;
		}

		const needsRegistration = code === 'REGISTRATION_REQUIRED'
			|| error.response?.status === 403;

		if (!needsRegistration) {
			console.error('Google sign-in failed:', error);
			goToLogin('Something went wrong, please try again later.');
			return;
		}

		// New-to-this-event Google member: pull the SSO profile to prefill the
		// attach form, then hand off to the registration custom-fields step.
		// Email verification is skipped — Google has already verified the email.
		let ssoUser = null;
		try {
			const profileRes = await axios.get('/api/sso/api/auth/user');
			ssoUser = profileRes.data?.data || profileRes.data || null;
		} catch (profileError) {
			console.error('Failed to load SSO profile for Google user:', profileError);
		}

		// Pre-screen Hub access (same gate as the email flow, admins exempt via
		// roles) so a blocked non-member is stopped here, not after filling the join form.
		if (ssoUser?.email) {
			try {
				const { data: validation } = await axios.post('/api/access/validate-email', {
					email: ssoUser.email,
					roles: ssoUser.roles || [],
				});
				if (validation?.data?.pending_request) {
					delete axios.defaults.headers.common['Authorization'];
					router.replace('/login?pending_request=1');
					return;
				}
				if (validation?.data?.allowed === false) {
					delete axios.defaults.headers.common['Authorization'];
					router.replace('/login?access_blocked=1');
					return;
				}
			} catch (gateError) {
				// Fail open — the join step still enforces the gate authoritatively.
				console.error('Access pre-screen failed for Google user:', gateError);
			}
		}

		authStore.setPendingTokenData({ access_token: token, expires_in: TOKEN_COOKIE_DAYS });
		authStore.setPendingSsoUser(ssoUser);
		authStore.setUserAlreadyExistEmail(ssoUser?.email || '');
		router.replace('/register');
	}
});
</script>

<template>
	<div class="google-callback">
		<Loading variant="lines" :rows="3" />
		<div class="google-callback__text">Signing you in…</div>
	</div>
</template>

<style lang="scss" scoped>
.google-callback {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 16px;
	min-height: 200px;

	&__text {
		font-family: 'Lato', sans-serif;
		font-size: 16px;
		color: #4B444D;
	}
}
</style>
